"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { iconRegistry } from "@pcln/horizon-icons";
import { motion, useReducedMotion } from "motion/react";
import { forwardRef, useState } from "react";
import { cn } from "../../utils/index.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { HorizonIcon } from "../HorizonIcon/index.js";
import { iconToggleButtonVariants } from "./IconToggleButton.variants.js";
const isHorizonIconName = (name)=>'string' == typeof name && Object.hasOwn(iconRegistry, name);
const isFavoriteIcon = (iconName)=>'favorite' === iconName;
const getIconSource = (iconName)=>{
    if (isFavoriteIcon(iconName)) return 'favorite';
    if (isHorizonIconName(iconName)) return 'horizon';
    return 'materialSymbol';
};
const HORIZON_OUTLINE_SUFFIX = 'Outline';
const getHorizonFilledName = (name)=>{
    if (!name.endsWith(HORIZON_OUTLINE_SUFFIX)) return name;
    const filledName = name.slice(0, -HORIZON_OUTLINE_SUFFIX.length);
    return Object.hasOwn(iconRegistry, filledName) ? filledName : name;
};
const getHorizonOutlineName = (name)=>{
    const filledName = getHorizonFilledName(name);
    const outlineName = `${filledName}${HORIZON_OUTLINE_SUFFIX}`;
    return Object.hasOwn(iconRegistry, outlineName) ? outlineName : void 0;
};
const resolveHorizonToggleIconName = (name, isToggledOn)=>{
    if (isToggledOn) return getHorizonFilledName(name);
    return getHorizonOutlineName(name) ?? name;
};
const hasHorizonOutlinePair = (name)=>void 0 !== getHorizonOutlineName(name);
const BURST_ANIMATIONS = [
    {
        id: 'burst-top-right-rotate-45',
        initial: {
            opacity: 1,
            scale: 0.5,
            rotate: 45
        },
        animate: {
            opacity: 0,
            scale: 0.5,
            x: 15,
            y: -15,
            rotate: 45
        }
    },
    {
        id: 'burst-top-left-rotate-neg-45',
        initial: {
            opacity: 1,
            scale: 0.5,
            rotate: -45
        },
        animate: {
            opacity: 0,
            scale: 0.5,
            x: -15,
            y: -15,
            rotate: -45
        }
    },
    {
        id: 'burst-bottom-center',
        initial: {
            opacity: 1,
            scale: 0.2
        },
        animate: {
            opacity: 0,
            scale: 0.2,
            y: 15
        }
    },
    {
        id: 'burst-bottom-right',
        initial: {
            opacity: 1,
            scale: 0.2
        },
        animate: {
            opacity: 0,
            scale: 0.2,
            x: 15,
            y: 5
        }
    },
    {
        id: 'burst-bottom-left',
        initial: {
            opacity: 1,
            scale: 0.2
        },
        animate: {
            opacity: 0,
            scale: 0.2,
            x: -15,
            y: 5
        }
    }
];
const BURST_TRANSITION = {
    duration: 0.5
};
const getBurstAnimate = (animation, spread = 1)=>({
        ...animation.animate,
        ...void 0 !== animation.animate.x && {
            x: animation.animate.x * spread
        },
        ...void 0 !== animation.animate.y && {
            y: animation.animate.y * spread
        }
    });
function AnimatedIconWithBurst({ size, isToggledOn, isAnimated, shouldReduceMotion, icon, burstIcon }) {
    const burstSpread = Number(size) / 24;
    return /*#__PURE__*/ jsxs("span", {
        className: "relative inline-flex items-center justify-center overflow-visible align-middle",
        style: {
            width: `${size}px`,
            height: `${size}px`
        },
        children: [
            isToggledOn && isAnimated && !shouldReduceMotion && BURST_ANIMATIONS.map((animation)=>/*#__PURE__*/ jsx(motion.span, {
                    className: "pointer-events-none absolute inset-0 flex items-center justify-center",
                    initial: animation.initial,
                    animate: getBurstAnimate(animation, burstSpread),
                    transition: BURST_TRANSITION,
                    children: burstIcon
                }, animation.id)),
            icon
        ]
    });
}
const HEART_FILL = 'M12 20.325C11.7667 20.325 11.5292 20.2833 11.2875 20.2C11.0458 20.1167 10.8333 19.9833 10.65 19.8L8.925 18.225C7.15833 16.6083 5.5625 15.0042 4.1375 13.4125C2.7125 11.8208 2 10.0667 2 8.14999C2 6.58333 2.525 5.27499 3.575 4.22499C4.625 3.17499 5.93333 2.64999 7.5 2.64999C8.38333 2.64999 9.21667 2.83749 10 3.21249C10.7833 3.58749 11.45 4.09999 12 4.74999C12.55 4.09999 13.2167 3.58749 14 3.21249C14.7833 2.83749 15.6167 2.64999 16.5 2.64999C18.0667 2.64999 19.375 3.17499 20.425 4.22499C21.475 5.27499 22 6.58333 22 8.14999C22 10.0667 21.2917 11.825 19.875 13.425C18.4583 15.025 16.85 16.6333 15.05 18.25L13.35 19.8C13.1667 19.9833 12.9542 20.1167 12.7125 20.2C12.4708 20.2833 12.2333 20.325 12 20.325Z';
const HEART_OUTLINE = 'M12 20.325C11.7667 20.325 11.5292 20.2833 11.2875 20.2C11.0458 20.1167 10.8333 19.9833 10.65 19.8L8.925 18.225C7.15833 16.6083 5.5625 15.0042 4.1375 13.4125C2.7125 11.8208 2 10.0667 2 8.14999C2 6.58333 2.525 5.27499 3.575 4.22499C4.625 3.17499 5.93333 2.64999 7.5 2.64999C8.38333 2.64999 9.21667 2.83749 10 3.21249C10.7833 3.58749 11.45 4.09999 12 4.74999C12.55 4.09999 13.2167 3.58749 14 3.21249C14.7833 2.83749 15.6167 2.64999 16.5 2.64999C18.0667 2.64999 19.375 3.17499 20.425 4.22499C21.475 5.27499 22 6.58333 22 8.14999C22 10.0667 21.2917 11.825 19.875 13.425C18.4583 15.025 16.85 16.6333 15.05 18.25L13.35 19.8C13.1667 19.9833 12.9542 20.1167 12.7125 20.2C12.4708 20.2833 12.2333 20.325 12 20.325ZM11.05 6.74999C10.5667 6.06666 10.05 5.54583 9.5 5.18749C8.95 4.82916 8.28333 4.64999 7.5 4.64999C6.5 4.64999 5.66667 4.98333 5 5.64999C4.33333 6.31666 4 7.14999 4 8.14999C4 9.01666 4.30833 9.93749 4.925 10.9125C5.54167 11.8875 6.27917 12.8333 7.1375 13.75C7.99583 14.6667 8.87917 15.525 9.7875 16.325C10.6958 17.125 11.4333 17.7833 12 18.3C12.5667 17.7833 13.3042 17.125 14.2125 16.325C15.1208 15.525 16.0042 14.6667 16.8625 13.75C17.7208 12.8333 18.4583 11.8875 19.075 10.9125C19.6917 9.93749 20 9.01666 20 8.14999C20 7.14999 19.6667 6.31666 19 5.64999C18.3333 4.98333 17.5 4.64999 16.5 4.64999C15.7167 4.64999 15.05 4.82916 14.5 5.18749C13.95 5.54583 13.4333 6.06666 12.95 6.74999C12.8333 6.91666 12.6917 7.04166 12.525 7.12499C12.3583 7.20833 12.1833 7.24999 12 7.24999C11.8167 7.24999 11.6417 7.20833 11.475 7.12499C11.3083 7.04166 11.1667 6.91666 11.05 6.74999Z';
function renderIconToggleContent({ iconName, size, isToggledOn, isAnimated, shouldReduceMotion, showBurst, iconBorderClasses, iconFillSlotClass, iconSlotClasses }) {
    if (isFavoriteIcon(iconName)) return /*#__PURE__*/ jsxs(motion.svg, {
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        xmlns: "http://www.w3.org/2000/svg",
        overflow: "visible",
        children: [
            showBurst && BURST_ANIMATIONS.map((animation)=>/*#__PURE__*/ jsx(motion.path, {
                    d: HEART_FILL,
                    className: iconFillSlotClass,
                    initial: animation.initial,
                    animate: animation.animate,
                    transition: BURST_TRANSITION
                }, animation.id)),
            /*#__PURE__*/ jsx(motion.path, {
                d: HEART_FILL,
                transform: "translate(12, 12) scale(0.9) translate(-12, -12)",
                className: iconFillSlotClass
            }),
            /*#__PURE__*/ jsx(motion.path, {
                d: HEART_OUTLINE,
                className: iconBorderClasses
            })
        ]
    });
    if (isHorizonIconName(iconName)) return /*#__PURE__*/ jsx(AnimatedIconWithBurst, {
        size: size,
        isToggledOn: isToggledOn,
        isAnimated: isAnimated,
        shouldReduceMotion: shouldReduceMotion,
        burstIcon: /*#__PURE__*/ jsx(HorizonIcon, {
            name: getHorizonFilledName(iconName),
            size: size,
            className: iconSlotClasses
        }),
        icon: /*#__PURE__*/ jsx(HorizonIcon, {
            name: resolveHorizonToggleIconName(iconName, isToggledOn),
            size: size,
            className: iconSlotClasses
        })
    });
    return /*#__PURE__*/ jsx(AnimatedIconWithBurst, {
        size: size,
        isToggledOn: isToggledOn,
        isAnimated: isAnimated,
        shouldReduceMotion: shouldReduceMotion,
        burstIcon: /*#__PURE__*/ jsx(CdnIcon, {
            iconName: iconName,
            size: size,
            className: iconSlotClasses
        }),
        icon: /*#__PURE__*/ jsx(CdnIcon, {
            iconName: iconName,
            size: size,
            className: iconSlotClasses
        })
    });
}
const IconToggleButton_IconToggleButton = /*#__PURE__*/ forwardRef(function({ disabled = false, onToggle, iconName = 'favorite', size = '32', ariaLabel = 'favorite', isAnimated = false, isToggledOn: isToggledOnProp, className, buttonClassName, slots = {
    buttonSlot: '',
    iconBorderSlot: '',
    iconFillSlot: '',
    iconSlot: ''
}, ...props }, ref) {
    const isControlled = void 0 !== isToggledOnProp;
    const [internalToggledOn, setInternalToggledOn] = useState(false);
    const isToggledOn = isControlled ? isToggledOnProp : internalToggledOn;
    const shouldReduceMotion = useReducedMotion();
    const iconSource = getIconSource(iconName);
    const hasOutlinePair = isHorizonIconName(iconName) ? hasHorizonOutlinePair(iconName) : true;
    const { buttonSlot, iconBorderSlot, iconFillSlot, iconSlot } = iconToggleButtonVariants({
        ...props,
        disabled,
        isToggledOn,
        iconSource,
        hasOutlinePair
    });
    const buttonClasses = cn(buttonSlot(), className, buttonClassName, slots.buttonSlot);
    const iconBorderClasses = cn(iconBorderSlot(), slots.iconBorderSlot);
    const iconFillSlotClass = cn(iconFillSlot(), slots.iconFillSlot);
    const iconSlotClasses = cn(iconSlot(), slots.iconSlot);
    const handleClick = (event)=>{
        if (disabled) return;
        const newToggledState = !isToggledOn;
        if (onToggle) onToggle(newToggledState, event);
        if (!isControlled) setInternalToggledOn(newToggledState);
    };
    const showBurst = isToggledOn && isAnimated && !shouldReduceMotion;
    const iconContent = renderIconToggleContent({
        iconName,
        size,
        isToggledOn,
        isAnimated,
        shouldReduceMotion,
        showBurst,
        iconBorderClasses,
        iconFillSlotClass,
        iconSlotClasses
    });
    return /*#__PURE__*/ jsx("button", {
        type: "button",
        className: buttonClasses,
        onClick: handleClick,
        disabled: disabled,
        "aria-pressed": isToggledOn,
        "aria-label": ariaLabel,
        ref: ref,
        children: iconContent
    });
});
IconToggleButton_IconToggleButton.displayName = 'IconToggleButton';
export { IconToggleButton_IconToggleButton as IconToggleButton };
